import gql from "graphql-tag";
import { atom, SetStateAction, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { TrendChartConfig } from "./FlightConfigTypes/TrendChartConfigType";
import {
  ReportsConfig,
  ReportRangeEnum,
  ReportSummaryMetric,
} from "./FlightConfigTypes/ReportPageConfigType";
import { GeneralConfigType } from "./FlightConfigTypes/GeneralConfigType";
import { GroupMetricType } from "./FlightConfigTypes/GroupMetricType";
import { HubletViewConfig } from "./FlightConfigTypes/HubletViewConfigType";
import {
  useGetPageFromPath,
  useSummaryGroup,
  useWorkScheduleIdOnCurrentPage,
} from "utils/customHooks/genericHooks";
import { useAuth0 } from "contexts/AuthManager/Auth";
import { useCallback, useMemo } from "react";
import { handleParseFlightConfigValue } from "./handleParseFlightConfigValue";
import { processConfigProperty } from "./handleMultiConfigValue";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { camelCase, cloneDeep, merge } from "lodash";
import { useParams } from "react-router";
import { ReportParams } from "reports/types";
import { filterObjectByRemovingKeys } from "utils/arrayUtils";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { mapEntries } from "@raven/shared/utils";

// Flight Config serves as home for long-term stable config, which can be overridden by the customer in the admin panel (whereas LaunchDarkly useFlagsWithDefaults is for short-term config / dark-code releases, or config which needs to be instantly responsive (flightConfig requires a page reload))

export type FlightConfigType = {
  /** added automatically by Flight to determine cacheability */
  ckey?: string;
  hubletView: HubletViewConfig;
  /** config for Reports pages -- it can have config at the top level that applies to all workschedules, plus nested workschedule-specific overrides */
  reportConfig: {
    [workScheduleId: string]: ReportsConfig;
  } | null;
  trendChart: TrendChartConfig;
  /** metrics which appear in GroupMetrics (Timeline page, Tiles pages, Hublet page), TrendChart > PnlChartPopup */
  metrics: { [workScheduleId: string]: GroupMetricType[] };
  general: GeneralConfigType;
  metricDescriptions: Record<string, string>;
};

export const FLIGHT_CONFIG_QUERY = gql`
  query FlightConfig {
    flightConfig {
      hubletView
      trendChart
      reportConfig
      metrics
      general
      metricDescriptions
    }
  }
`;

const DEFAULT_GENERAL_CONFIG: GeneralConfigType = {
  trendsPageEnableGroupMetrics: false,
  reportsEnabled: false,
  operatorPageEnabled: false,
  expandedProcessTileOperatorName: false,
  expandedProcessTileSkuId: true,
  expandedProcessTileSkuName: true,
  expandedProcessTileCount: true,
  expandedProcessTileCountCustomKey: null,
  expandedProcessTileAheadBehindProcesses: [],
  expandedProcessTileTeamSize: true,
  tileWidthMultiplier: 1,
  expandedProcessTileTopLosses: true,
  accessToTrendsPage: true,
  autoRedirectIntoFirstGroup: false,
  isAllGroupSerial: false,
  redirectFromHubletViewPageToGridPage: false,
  enableMachineBreakdownChart: false,
  enableSecondaryProcessesPnlChart: false,
  retrotaggingEnabled: false,
  lotInfoModuleUseTargetRateInsteadOfCurrentRate: false,
  enableLotInfoModule: false,
  showSkuTimeline: true,
  showCountTimeline: true,
  showRejectCountTimeline: false,
  showOperatorTimeline: true,
  trendsPageShowTagHighlight: false,
  timelinePageShowTagHighlight: false,
  overrideDdaLimitDays: 0,
  timelineTooltipShowCounts: false,
  aheadBehindShowHeadCount: false,
  aheadBehindScaleTargetRate: {},
  aheadBehindScaleTargetRateColorOnly: {},
  showProcessGroupSwitcherDropdownCycler: false,
  operatorPageEnableRangeSelector: false,
  timelineTooltipEnableSignalValidation: true,
  customCss: "",
  customCssByProcess: {
    example_d_001: ".timeline-tooltip-counts { display: none; };",
  },
  customCssByProcessGroup: {
    "Example Group Name": ".timeline-tooltip-counts { display: none; };",
  },
  trendsPageMaxQueryRangeDays: 90,
  // start by allowing 10 years back from current year, override in flightConfig if needed
  trendsPageMinDateMs: new Date(
    `Jan 1 ${new Date().getFullYear() - 10}`
  ).getTime(),
  wifiBarsUpdateIntervalMs: 5000,
  hideProducingLosses: false,
  convertQualityLossToProducing: false,
  includeDependentLosses: true, // TECHDEBT true for now to maintain current behaviour, eventually switch to false
  topDownOrBottomUp: "default",
  hideProcessTiles: [],
  multipleTimelinesOnGridPage: [],
  extraPnlChartColumns: [],
  timelineShrinkSegmentCategories: [],
  gridPageLinkToDashboardPage: false,
  multiplyNetworkRequestIntervals: 1,
  whitelabel: {
    logoUrl: undefined,
  },
  disableAnimations: false,
  expandedProcessTileCompactMode: false,
  autoSetTimezoneOverride: false,
  trendsPageOverrideGe: false,
  powerBiReportId: "",
  powerBiReportIdThroughput: "",
  powerBiReportIdLogbook: "",
  /* Array of Power BI reports with their IDs and display names to render as links */
  powerBiReports: [{ id: "", name: "" }],
  powerBiUpdates: {
    analytics: {
      mailto: "",
      markdown: "",
    },
    throughput: {
      mailto: "",
      markdown: "",
    },
    logbook: {
      mailto: "",
      markdown: "",
    },
  },
  workSchedulesSortOrder:
    [] /** Use producingTime from dataDumpAggregate, rather than calculating producingTime from effectiveProducingTime and producing losses */,
  producingTimeFromDataDumpAggregate: true,
  accessToSightAnalytics: false,
  dayStartMsOffset: null,
  timelineTooltipAdditionalMetrics: [],
};

/** flight config: one-per-customer configurations for flight */
export const useFlightConfig = (): [
  FlightConfigType & { metricsRawByWorkSchedule: FlightConfigType["metrics"] },
  (update: SetStateAction<FlightConfigType>) => void | Promise<void>,
  FlightConfigType,
] => {
  const [flightConfigRaw, setFlightConfigRaw] = useAtom(flightConfigRawAtom);

  const { processGroupName } = useParams<ReportParams>();

  const { flightConfigOverridesFromLd } = useFlagsWithDefaults();

  const workScheduleIdOnCurrentPage = useWorkScheduleIdOnCurrentPage();

  // config sections can have workschedule-specific overrides like this: { "1": { "dayStartMsOffset": 0 }, "2": { "dayStartMsOffset": -3600000 } }
  // so for each config section, look for workschedule-specific overrides and fold them into the top-level config
  const flightConfigWithWS = useMemo(() => {
    const general = flightConfigRaw.general;
    const trendChart = flightConfigRaw.trendChart;
    const hubletView = flightConfigRaw.hubletView;

    // for each of the above configs, check if there is a string-number keys like "1", that matches current workschedule id, this contains workschedule-specific overrides -- if so, fold it into the top-level config
    const generalForWorkSchedule = general[workScheduleIdOnCurrentPage];
    const trendChartForWorkSchedule = trendChart[workScheduleIdOnCurrentPage];
    const hubletViewForWorkSchedule = hubletView[workScheduleIdOnCurrentPage];

    return {
      general: generalForWorkSchedule
        ? { ...general, ...generalForWorkSchedule }
        : general,
      trendChart: trendChartForWorkSchedule
        ? { ...trendChart, ...trendChartForWorkSchedule }
        : trendChart,
      hubletView: hubletViewForWorkSchedule
        ? { ...hubletView, ...hubletViewForWorkSchedule }
        : hubletView,
      reportConfig: flightConfigRaw.reportConfig, // handled separately below
      metrics: flightConfigRaw.metrics, // handled separately below
      metricDescriptions: flightConfigRaw.metricDescriptions, // generated by Midgard, no need to handle here
    };
  }, [flightConfigRaw, workScheduleIdOnCurrentPage]);

  const parseFlightConfigValue = useParseFlightConfigValue();
  const processMultiConfigValue = useProcessMultiConfigValue();
  const flightConfigParsed: FlightConfigType & {
    metricsRawByWorkSchedule: FlightConfigType["metrics"];
  } = useMemo(() => {
    const metricsRawCleaned = mapEntries(
      flightConfigWithWS.metrics,
      ([key, value]: [
        keyof FlightConfigType["metrics"],
        GroupMetricType[],
      ]) => [key, value?.map?.(cleanMetric) ?? value]
    );
    const metricsBySchedule = metricsRawCleaned
      ? // metrics used to be an array, handle this legacy config type until fully migrated
        Array.isArray(metricsRawCleaned)
        ? { "1": metricsRawCleaned }
        : metricsRawCleaned
      : {};

    // if a metric has "instances" key, we need to create one metric for each instance, with specified overrides
    const metricsByScheduleWithInstances = Object.entries(
      metricsBySchedule
    ).reduce((acc, [workScheduleId, metrics]) => {
      const metricsInstanced = metrics?.reduce?.((acc, metric) => {
        if (metric.instances) {
          return [
            ...acc,
            ...metric.instances.map((instance, idx) => ({
              __instanceIdx: idx,
              ...filterObjectByRemovingKeys(metric, ["instances"]),
              ...instance,
            })),
          ];
        } else {
          return [...acc, metric];
        }
      }, []);
      return {
        ...acc,
        [workScheduleId]: metricsInstanced,
      };
    }, {});

    // parse+fallback for each item in the default config
    const general = flightConfigWithWS.general
      ? {
          ...flightConfigWithWS.general,
          ...(Object.entries(DEFAULT_GENERAL_CONFIG).reduce(
            (acc, [key, val]) => {
              let value = val;
              if (key in flightConfigWithWS.general) {
                const configValue = flightConfigWithWS.general[key];
                if (typeof configValue === "string") {
                  value = parseFlightConfigValue(configValue);
                } else if (typeof configValue === "object") {
                  value = cloneDeep(configValue);
                } else {
                  value = configValue;
                }
              }
              acc[key] = value;
              return acc;
            },
            {}
          ) as GeneralConfigType),
        }
      : DEFAULT_GENERAL_CONFIG;

    const trendChart = flightConfigWithWS.trendChart
      ? {
          ...flightConfigWithWS.trendChart,
          ...(Object.entries(DEFAULT_TREND_CHART_CONFIG).reduce(
            (acc, [key, val]) => {
              let value = val;
              if (key in flightConfigWithWS.trendChart) {
                const configValue = flightConfigWithWS.trendChart[key];
                if (typeof configValue === "string") {
                  value = parseFlightConfigValue(configValue);
                } else {
                  value = configValue;
                }
              }
              acc[key] = value;
              return acc;
            },
            {}
          ) as TrendChartConfig),
        }
      : DEFAULT_TREND_CHART_CONFIG;
    const hubletView = flightConfigWithWS.hubletView
      ? {
          ...flightConfigWithWS.hubletView,
          ...(Object.entries(DEFAULT_HUBLET_VIEW_CONFIG).reduce(
            (acc, [key, val]) => {
              let value = val;
              if (key in flightConfigWithWS.hubletView) {
                const configValue = flightConfigWithWS.hubletView[key];
                if (typeof configValue === "string") {
                  value = parseFlightConfigValue(configValue);
                } else {
                  value = configValue;
                }
              }
              acc[key] = value;
              return acc;
            },
            {}
          ) as HubletViewConfig),
        }
      : DEFAULT_HUBLET_VIEW_CONFIG;

    // the raw config in Midgard actually has type Partial<ReportsConfig> & { [workScheduleId: string]: Partial<ReportsConfig> }
    // e.g. { "1": { "machineGroups": ... }, "eosReportGeneralConfig": { ... } }
    // where some config is nested by workschedule ( "1": {...} ) and some is at the top level which applies to all workschedules, this allows deduplication of config
    // so for simplicity, here we'll merge these together into the type { [workScheduleId: string]: ReportsConfig }

    // first isolate the top-level config by removing any workschedule-specific configs and vice-versa
    const { reportConfigTopLevelOnly, reportConfigByWorkscheduleOnly } =
      Object.entries(flightConfigWithWS.reportConfig ?? {}).reduce(
        (acc, [key, val]) => {
          // check that key is a string of a number => it's a workschedule config
          const isWorkScheduleConfig = !isNaN(Number(key));
          if (isWorkScheduleConfig) {
            acc.reportConfigByWorkscheduleOnly[key] = val as ReportsConfig;
          } else {
            // else it's a top-level config
            acc.reportConfigTopLevelOnly[key] = val;
          }
          return acc;
        },
        {
          reportConfigTopLevelOnly: {} as Partial<ReportsConfig>,
          reportConfigByWorkscheduleOnly: {} as {
            [workScheduleId: string]: ReportsConfig | null;
          },
        }
      );

    // then, merge the top-level config into each workschedule-specific config
    const reportConfigMerged: { [workScheduleId: string]: ReportsConfig } =
      Object.entries(reportConfigByWorkscheduleOnly).reduce(
        (acc, [workScheduleId, wsSpecificConfig]) => {
          acc[workScheduleId] = {
            ...DEFAULT_REPORT_CONFIG,
            ...reportConfigTopLevelOnly,
            ...wsSpecificConfig,
          };
          return acc;
        },
        {} as { [workScheduleId: string]: ReportsConfig }
      );

    // now that we have a single object with all workschedule-specific configs, we need to parse each value
    const reportConfigParsed = Object.entries(reportConfigMerged).reduce(
      (acc, [workScheduleId, config]) => {
        const parsed = Object.entries(config).reduce((acc, [key, val]) => {
          let value = val;
          if (typeof val === "string") {
            value = parseFlightConfigValue(val);
          }
          acc[key] = value;
          return acc;
        }, {} as ReportsConfig);
        acc[workScheduleId] = parsed;
        return acc;
      },
      {} as { [workScheduleId: string]: ReportsConfig }
    );

    // and we have to filter the report metrics
    const reportConfigWithFilteredMetrics = Object.entries(
      reportConfigParsed
    ).reduce(
      (acc, [workScheduleId, config]) => {
        const eosReportMetricsConfig = config.eosReportMetricsConfig;
        const summaryMetricsRow1 = filterReportMetrics({
          metricsUnfiltered: eosReportMetricsConfig?.summaryMetricsRow1 ?? [],
          processGroupName,
        }) as Partial<ReportSummaryMetric>[];
        const summaryMetricsRow2 = filterReportMetrics({
          metricsUnfiltered: eosReportMetricsConfig?.summaryMetricsRow2 ?? [],
          processGroupName,
        }) as Partial<ReportSummaryMetric>[];
        const summaryMetricsInputList = filterReportMetrics({
          metricsUnfiltered:
            eosReportMetricsConfig?.summaryMetricsInputList ?? [],
          processGroupName,
        }) as Partial<ReportSummaryMetric>[];

        const reportConfigWithFilteredMetrics = {
          ...config,
          eosReportMetricsConfig: {
            ...eosReportMetricsConfig,
            summaryMetricsRow1,
            summaryMetricsRow2,
            summaryMetricsInputList,
          },
        };

        acc[workScheduleId] = reportConfigWithFilteredMetrics;
        return acc;
      },
      {} as { [workScheduleId: string]: ReportsConfig }
    );

    // if a metric in eosReportMetricsConfig has "instances" key, we need to create one metric for each instance, with specified overrides
    function instanceReportMetric(
      metric: ReportSummaryMetric
    ): ReportSummaryMetric[] {
      if ("instances" in metric && metric.instances) {
        return metric.instances.map((instance, idx) => ({
          __instanceIdx: idx,
          ...filterObjectByRemovingKeys(metric, ["instances"]),
          ...instance,
        }));
      } else {
        return [metric];
      }
    }
    function instanceReportMetrics(
      metrics?: ReportSummaryMetric[]
    ): ReportSummaryMetric[] {
      return (
        metrics?.reduce((acc, metric) => {
          // if the metric has "instances", add one instance for each item in the instances array
          const metricInstances = instanceReportMetric(metric);
          return [...acc, ...metricInstances];
        }, [] as ReportSummaryMetric[]) ?? []
      );
    }

    // do this for each workschedule config in reportConfigWithFilteredMetrics
    const reportConfigWithInstances: {
      [workScheduleId: string]: ReportsConfig;
    } = Object.entries(reportConfigWithFilteredMetrics).reduce(
      (acc, [workScheduleId, config]) => {
        acc[workScheduleId] = {
          ...config,
          eosReportMetricsConfig: {
            summaryMetricsRow1: instanceReportMetrics(
              config.eosReportMetricsConfig?.summaryMetricsRow1
            ),
            summaryMetricsRow2: instanceReportMetrics(
              config.eosReportMetricsConfig?.summaryMetricsRow2
            ),
            summaryMetricsInputList: instanceReportMetrics(
              config.eosReportMetricsConfig?.summaryMetricsInputList
            ),
          },
        };
        return acc;
      },
      {} as { [workScheduleId: string]: ReportsConfig }
    );

    const parsed = {
      ...flightConfigWithWS,
      metrics: metricsByScheduleWithInstances,
      metricsRawByWorkSchedule: metricsBySchedule,
      general,
      trendChart,
      hubletView,
      reportConfig: reportConfigWithInstances,
    };

    const merged = merge(parsed, flightConfigOverridesFromLd ?? {});

    // merge individual config sections from url
    const urlParams = new URLSearchParams(window.location.search);
    const flightConfigOverridesFromUrl = {
      // e.g. only show OEE: ?flightConfigMetrics={"1":[{"metricId":"1","displayName":"OEE","dataDumpKey":"oee","units":"%","decimalPoints":2 }]}
      metrics: urlParams.get("flightConfigMetrics")
        ? JSON.parse(urlParams.get("flightConfigMetrics") as string)
        : null,
      // e.g. enable reports page: ?flightConfigGeneral={"reportsEnabled":true}
      general: urlParams.get("flightConfigGeneral")
        ? JSON.parse(urlParams.get("flightConfigGeneral") as string)
        : null,
      trendChart: urlParams.get("flightConfigTrendChart")
        ? JSON.parse(urlParams.get("flightConfigTrendChart") as string)
        : null,
      hubletView: urlParams.get("flightConfigHubletView")
        ? JSON.parse(urlParams.get("flightConfigHubletView") as string)
        : null,
      reportConfig: urlParams.get("flightConfigReportConfig")
        ? JSON.parse(urlParams.get("flightConfigReportConfig") as string)
        : null,
    };

    // if metrics are specified in the url, use only those instead of merging
    merged.metrics = flightConfigOverridesFromUrl.metrics ?? merged.metrics;
    // other configs get merged from the url
    merged.general = merge(
      merged.general,
      flightConfigOverridesFromUrl.general
    );
    merged.trendChart = merge(
      merged.trendChart,
      flightConfigOverridesFromUrl.trendChart
    );
    merged.hubletView = merge(
      merged.hubletView,
      flightConfigOverridesFromUrl.hubletView
    );
    merged.reportConfig = merge(
      merged.reportConfig,
      flightConfigOverridesFromUrl.reportConfig
    );

    // apply all multi-config properties
    const processedConfig =
      processMultiConfigValue.processAllProperties(merged);

    return processedConfig;
  }, [
    flightConfigWithWS,
    parseFlightConfigValue,
    processMultiConfigValue,
    processGroupName,
    flightConfigOverridesFromLd,
  ]);

  return [flightConfigParsed, setFlightConfigRaw, flightConfigRaw];
};

function filterReportMetrics({
  metricsUnfiltered,
  processGroupName,
}: {
  metricsUnfiltered: any[];
  processGroupName?: string;
}) {
  if (!processGroupName) {
    return metricsUnfiltered;
  }
  return metricsUnfiltered.filter(
    metric =>
      (metric.onlyShowForGroupIds && Array.isArray(metric.onlyShowForGroupIds)
        ? metric.onlyShowForGroupIds
            .map(t => t.toLowerCase())
            .includes(processGroupName.toLowerCase())
        : true) &&
      (metric.hideForGroupIds && Array.isArray(metric.hideForGroupIds)
        ? !metric.hideForGroupIds
            .map(t => t.toLowerCase())
            .includes(processGroupName.toLowerCase())
        : true)
  );
}

export const useMetricsForWorkSchedule = (): GroupMetricType[] => {
  const [{ metrics: allFlightConfigMetrics }] = useFlightConfig();

  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  return useMemo(() => {
    const flightConfigMetrics = Array.isArray(
      allFlightConfigMetrics[workScheduleId]
    )
      ? allFlightConfigMetrics[workScheduleId]
      : [];
    return flightConfigMetrics;
  }, [allFlightConfigMetrics, workScheduleId]);
};

const DEFAULT_TREND_CHART_CONFIG: TrendChartConfig = {
  target: null,
  targetByProcess: {},
  outputLabel: null,
  outputLabelByProcess: {},
  datadumpKey: null,
  datadumpKeyByProcess: {},
  maxOutput: null,
  extraLossCategories: [],
  hiddenCategories: [],
  hideNumbersOnSegmentStacks: false,
  hideNumbersOnSegments: false,
  showAxes: true,
  showTrendLine: false,
  showTargetTrendLine: false,
  showCounts: true,
  hideProfitCategories: false,
  trafficLightsTargetsPerProcess: {},
  overrideCountMachemetryIdsPerGroup: {},
};

const DEFAULT_HUBLET_VIEW_CONFIG: HubletViewConfig = {
  useRunRateInsteadOfCurrentRate: false,
  bottomRowItems: null,
  bottomRowItemsByProcess: {},
  autoRotateBetweenProcesses: false,
  showCountsSinceShiftStartOnly: false,
  hideTargetRate: false,
  hideTargetSoFar: false,
  hideTargetsRightSide: false,
  hideCountsLeftSide: false,
  hideSku: false,
  shouldExcludeSlowdownsAndMinorStopsInRateDenominator: false,
  targetOutputIgnorePlanned: false,
};

export const DEFAULT_REPORT_CONFIG: ReportsConfig = {
  /** When this customer was deployed, or certain configs for valid data to be showing on reports
   * This is only needed if they are using the ReportsPage.tsx
   * (in milliseconds)
   */
  startTime: undefined,
  /** set to true if customer just wants daily or weekly */
  shouldSkipEOS: undefined,
  /** true by default, set to false if we want to see Not Schedule shift reports */
  shouldSkipNPS: undefined,
  /** list of machineGroups to show reports for */
  machineGroups: [],
  timeZone: undefined,
  /** false by default, groups dropdown by machine group */
  shouldGroupByMachineGroup: false,
  dailyReportConfig: {
    startHour: 0,
    startMinute: 0,

    /** if not included, use startHour */
    endHour: undefined,
    /** if not included, use startMinute */
    endMinute: undefined,

    /** list of machineGroups to show daily reports, if not included use machineGroups */
    machineGroups: [],
    /** use custom report title if supplied */
    reportTitle: undefined,
  },
  weeklyReportConfig: undefined,
  performanceReportConfig: {
    /** Performance Report are of two types : Weekly and Monthly */
    weekly: undefined,
  },
  /** CSS applied to reports pages */
  customCss: "",
  /** configure the EOSReport */
  eosReportMetricsConfig: {
    summaryMetricsRow1: [],
    summaryMetricsRow2: [],
    summaryMetricsInputList: [],
  },
  eosReportGeneralConfig: {
    shouldShowPrint: false,
    shouldSkipSkuData: false,
    shouldUseProducingLosses: false,
    producingLossesToSkip: [],
    /** appears in first row of report header */
    customHeaderTitle: undefined,
    /** appears in second row of report header */
    customReportTitle: undefined,
    /** show `${customTitlePrefix} - ${processGroupName}` in report header (overrides `customReportTitle`) */
    customTitlePrefix: undefined,
    customTitlePrefixByGroup: { example_process_group_name: "Example" },
    flightTimelineLinkGroup: undefined,
    flightDashboardLinkGroup: undefined,
    topDowntimesReports: [
      {
        enabled: false,
        reportRanges: [ReportRangeEnum.day, ReportRangeEnum.week],
        customTitle: undefined,
        categoriesToSkip: [],
        numberOfDowntimes: undefined,
        shouldShowHideLessThan1Percent: undefined,
        xLabel: undefined,
        timeDivisorMs: undefined,
        showDurationLabel: undefined,
      },
    ],
    trendChart: {
      summaryMetricsInputList: [
        {
          units: "%",
          dataDumpKey: "teepPercent",
          displayName: "TEEP",
        },
        {
          units: "%",
          dataDumpKey: "availabilityPercent",
          displayName: "Availability",
        },
        {
          units: "%",
          dataDumpKey: "uptimePercent",
          displayName: "Uptime",
        },
        {
          units: "%",
          dataDumpKey: "taggingPercent",
          displayName: "Tagging",
        },
      ],
      descendingOrder: false,
    },
    /** show "view report" button in MetricAndPnLBreakdowns component e.g. Shift Breakdowns chart rows */
    showReportLinks: true,
  },
};

export const EMPTY_FLIGHT_CONFIG: FlightConfigType = {
  trendChart: DEFAULT_TREND_CHART_CONFIG,
  hubletView: DEFAULT_HUBLET_VIEW_CONFIG,
  reportConfig: { "1": DEFAULT_REPORT_CONFIG },
  metrics: { "1": [] },
  general: DEFAULT_GENERAL_CONFIG,
  metricDescriptions: {},
};

const flightConfigRawAtom = atomWithStorage<
  FlightConfigType & {
    reportConfig:
      | ({ [workScheduleId: string]: ReportsConfig } & Partial<ReportsConfig>)
      | null;
  }
>("flightConfig", EMPTY_FLIGHT_CONFIG);

type isFlightConfigInitializedType =
  /** still fetching & haven't received a flightConfig */
  | "unknown"
  /** received flightConfig */
  | "initialized"
  /** failed to received flightConfig */
  | "uninitialized";
const isFlightConfigInitializedAtom =
  atom<isFlightConfigInitializedType>("unknown");
/** whether the customer's flightConfig is populated from Midgard */
export const useIsFlightConfigInitialized = (): [
  isFlightConfigInitializedType,
  (
    update: SetStateAction<isFlightConfigInitializedType>
  ) => void | Promise<void>,
] => {
  return useAtom(isFlightConfigInitializedAtom);
};

/** parse flightConfig to appropriate types e.g. JSON strings to proper object types, and/or strings to numbers */
export function parseFlightConfig(
  config: Omit<
    FlightConfigType,
    | "hubletView"
    | "trendChart"
    | "reportConfig"
    | "customMetrics"
    | "metrics"
    | "general"
    | "metricDescriptions"
  > & {
    // hubletView: string | null;
    hubletView: string | null;
    reportConfig: string | null;
    trendChart: string | null;
    metrics: string | null;
    general: string | null;
    metricDescriptions: string | null;
  }
): FlightConfigType {
  const hubletView = config.hubletView ? JSON.parse(config.hubletView) : null;
  const reportConfig = config.reportConfig
    ? JSON.parse(config.reportConfig)
    : null;
  const trendChart = config.trendChart
    ? parseTrendChartConfig(config.trendChart)
    : DEFAULT_TREND_CHART_CONFIG;
  const metrics = config.metrics ? JSON.parse(config.metrics) : [];
  const general = config.general ? JSON.parse(config.general) : [];
  const metricDescriptions = config.metricDescriptions
    ? JSON.parse(config.metricDescriptions)
    : {};
  // convert all keys to camelCase
  const metricDescriptionsCamel = mapEntries(
    metricDescriptions,
    ([key, value]) => [camelCase(key), value]
  );

  return {
    ...config,
    hubletView,
    reportConfig,
    trendChart,
    metrics,
    general,
    metricDescriptions: metricDescriptionsCamel,
  };
}

function parseTrendChartConfig(config: string): TrendChartConfig {
  const parsed = JSON.parse(config);
  const target = parsed.target ? Number(parsed.target) : null;
  const targetByProcess = parsed.targetByProcess
    ? convertObjectValuesToNumbers(parsed.targetByProcess)
    : null;
  const outputLabelByProcess = parsed.outputLabelByProcess
    ? parsed.outputLabelByProcess
    : null;
  const datadumpKeyByProcess = parsed.datadumpKeyByProcess
    ? parsed.datadumpKeyByProcess
    : null;
  const maxOutput = parsed.maxOutput ? Number(parsed.maxOutput) : null;
  const overrideCountMachemetryIdsPerGroup =
    parsed.overrideCountMachemetryIdsPerGroup
      ? parsed.overrideCountMachemetryIdsPerGroup
      : null;

  return {
    ...parsed,
    target,
    targetByProcess,
    outputLabelByProcess,
    datadumpKeyByProcess,
    maxOutput,
    overrideCountMachemetryIdsPerGroup,
  };
}

function convertObjectValuesToNumbers(
  object: Record<string, unknown>
): Record<string, number> {
  const newObject: Record<string, number> = {};
  Object.keys(object).forEach(key => {
    newObject[key] = Number(object[key]);
  });
  return newObject;
}

export const FLIGHT_CONFIG_DESCRIPTIONS: Record<
  keyof FlightConfigType,
  string | React.JSX.Element
> = {
  ckey: `Customer key`,
  general: (
    <p>
      General config -{" "}
      <a
        href="https://gitlab.com/raventelemetry/services/rookery/-/blob/main/apps/flight/src/components/DataGetter/FlightConfig/FlightConfigTypes/GeneralConfigType.tsx"
        target="_blank"
        rel="noopener noreferrer"
      >
        📚 general config docs
      </a>
    </p>
  ),
  hubletView: (
    <p>
      Hublet View config -{" "}
      <a
        href="https://gitlab.com/raventelemetry/services/rookery/-/blob/main/apps/flight/src/components/DataGetter/FlightConfig/FlightConfigTypes/HubletViewConfigType.tsx"
        target="_blank"
        rel="noopener noreferrer"
      >
        📚 hublet view config docs
      </a>
      {/* TODODAniel */}
    </p>
  ),

  metrics: (
    <p>
      determines which metrics are displayed in Group Metrics -{" "}
      <a
        href="https://gitlab.com/raventelemetry/services/rookery/-/blob/main/apps/flight/src/components/DataGetter/FlightConfig/FlightConfigTypes/GroupMetricType.tsx"
        target="_blank"
        rel="noopener noreferrer"
      >
        📚 metrics config docs
      </a>
    </p>
  ),
  reportConfig: (
    <p>
      determines which report links are displayed on Reports page -{" "}
      <a
        href="https://gitlab.com/raventelemetry/services/rookery/-/blob/main/apps/flight/src/components/DataGetter/FlightConfig/FlightConfigTypes/ReportPageConfigType.tsx"
        target="_blank"
        rel="noopener noreferrer"
      >
        📚 report config docs
      </a>
    </p>
  ),
  trendChart: (
    <p>
      configure the Trend Chart -{" "}
      <a
        href="https://gitlab.com/raventelemetry/services/rookery/-/blob/main/apps/flight/src/components/DataGetter/FlightConfig/FlightConfigTypes/TrendChartConfigType.tsx"
        target="_blank"
        rel="noopener noreferrer"
      >
        📚 trend chart config docs
      </a>
    </p>
  ),
  metricDescriptions:
    "descriptions for each metric by dataDumpKey (auto-generated)",
};

/**
 * List of config property paths that should be processed with multi-config functionality.
 * Add new properties here to extend multi-config support to more config values.
 */
const MULTI_CONFIG_PROPERTIES = [
  "general.customCss",
  "general.retrotaggingEnabled",
  // Add more properties here as needed
];

/** allow config like [email=daniel@raventelemetry.com;page=trends] to set true only if the specified conditions are all matched
 *
 * - e.g. "[email=bob@cat.com]" = only true when user has this email
 * - e.g. "[page=trends]" = only true on trends page
 * - e.g. "[process=raveng_d_001,raveng_d_002;page=trends;email=bob@cat.com]" = only true when we're on the trends page for process raveng_d_001 or raveng_d_002 and the user's email is bob@cat.com
 *
 * refer to handleParseFlightConfigValue.test.tsx for more examples */
function useParseFlightConfigValue() {
  const { user } = useAuth0();
  const { pageName } = useGetPageFromPath();
  const currentProcessId = useProcessIdOnSingleProcessPage();
  const currentGroupId = useSummaryGroup();
  const currentWorkScheduleId = useWorkScheduleIdOnCurrentPage();
  return useCallback(
    (value: any) =>
      handleParseFlightConfigValue(value, {
        pageName,
        user,
        currentProcessId,
        currentGroupId,
        currentWorkScheduleId,
      }),
    [pageName, user, currentProcessId, currentGroupId, currentWorkScheduleId]
  );
}

/**
 * Hook to process multi-config values that use condition keys like "[email=bob@cat.com]"
 * to determine which value to use based on current context.
 */
function useProcessMultiConfigValue() {
  const { user } = useAuth0();
  const { pageName } = useGetPageFromPath();
  const currentProcessId = useProcessIdOnSingleProcessPage();
  const currentGroupId = useSummaryGroup();
  const currentWorkScheduleId = useWorkScheduleIdOnCurrentPage();

  const context = useMemo(
    () => ({
      pageName,
      user,
      currentProcessId,
      currentGroupId,
      currentWorkScheduleId,
    }),
    [pageName, user, currentProcessId, currentGroupId, currentWorkScheduleId]
  );

  return {
    /**
     * Process a single config property path with multi-config rules
     */
    processSingleProperty: useCallback(
      <T extends Record<string, any>>(config: T, propertyPath: string): T =>
        processConfigProperty(config, propertyPath, context),
      [context]
    ),

    /**
     * Process multiple config properties with multi-config rules
     */
    processAllProperties: useCallback(
      <T extends Record<string, any>>(
        config: T,
        propertyPaths: string[] = MULTI_CONFIG_PROPERTIES
      ): T => {
        let processedConfig = { ...config };
        for (const path of propertyPaths) {
          processedConfig = processConfigProperty(
            processedConfig,
            path,
            context
          );
        }
        return processedConfig;
      },
      [context]
    ),
  };
}

function cleanMetric(metric: GroupMetricType) {
  return {
    ...metric,
    // allow decimalPoints to be string or number e.g. "1" or 1 are both valid
    decimalPoints: metric.decimalPoints && Number(metric.decimalPoints),
  };
}

/** gets the flightConfig.reportConfig matching the current work schedule */
export function useReportConfig() {
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  const [{ reportConfig }] = useFlightConfig();
  return reportConfig?.[workScheduleId];
}
